import React from "react";
import { useLocation } from "react-router-dom";
import { SIZE_KEY_MAP, SIZE_ORDER } from "./constants";

export const baseURL = () => {
  let baseURL = "";
  if (process.env.NODE_ENV === "production") {
    baseURL = "https://www.greygullapparel.com";
  } else {
    console.log("Running in development mode");
    baseURL = "http://localhost:3000";
  }
  return baseURL;
};

export const serverURL = () => {
  if (process.env.NODE_ENV === "production") {
    return process.env.REACT_APP_SERVER_URL;
  } else {
    console.log("Running in development mode");
    return process.env.REACT_APP_SERVER_URL_DEV;
  }
};

export const apiRequest = async ({ endpoint, method = "GET", token, body, isFormData = false }) => {
  const response = await fetch(`${serverURL()}${endpoint}`, {
    method,
    headers: {
      ...(!isFormData && { "Content-Type": "application/json" }),
      ...(token && { authorization: `Bearer ${token}` }),
    },
    ...(body && {
      body: isFormData ? body : JSON.stringify(body),
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || "Something went wrong");
  }

  return data;
};

export const useQuery = () => {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
};

export const isAlphaNumeric = (value) => {
  if (typeof value === "string") {
    return /^[a-zA-Z0-9]+$/.test(value);
  } else if (typeof value === "number") {
    return /^[a-zA-Z0-9]+$/.test(value);
  } else if (Array.isArray(value)) {
    let result = true;
    value.forEach((x) => {
      const test = /^[a-zA-Z0-9]+$/.test(x);
      if (!test) {
        result = false;
      }
    });
    return result;
  }
};

export const convertDate = (isoDate) => {
  if (!isoDate) return null;
  const date = new Date(isoDate);
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

function isoToUnixTimestamp(isoDate) {
  const date = new Date(isoDate);
  return Math.floor(date.getTime() / 1000);
}

export const hasReturnWindowExpired = (date) => {
  // CALCULATE WHETHER TO DISPLAY RETURN BUTTON OR NOT
  const returnWindow = 15 * 24 * 60 * 60; // 15 days
  const today = Math.ceil(Date.now() / 1000); // today's unix time in seconds
  const orderDate = isoToUnixTimestamp(date);
  const timePassed = today - orderDate;
  return timePassed > returnWindow;
};

export const capitalizeAddress = (obj) => {
  const capitalizedObj = {};

  for (const key in obj) {
    if (typeof obj[key] === "string" && key !== "state") {
      capitalizedObj[key] = obj[key]
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    } else {
      capitalizedObj[key] = obj[key]; // leave non-string values unchanged
    }
  }

  return capitalizedObj;
};

// make sure to account for New Hanover
// should I check for new_hanover or newhanover?

export const ncSalesTaxRates = {
  alamance: 0.0675,
  alexander: 0.07,
  alleghany: 0.07,
  anson: 0.07,
  ashe: 0.07,
  avery: 0.0675,
  beaufort: 0.0675,
  bertie: 0.07,
  bladen: 0.0675,
  brunswick: 0.0675,
  buncombe: 0.07,
  burke: 0.0675,
  cabarrus: 0.07,
  caldwell: 0.0675,
  camden: 0.0675,
  carteret: 0.0675,
  caswell: 0.0675,
  catawba: 0.07,
  chatham: 0.07,
  cherokee: 0.07,
  chowan: 0.0675,
  clay: 0.07,
  cleveland: 0.0675,
  columbus: 0.0675,
  craven: 0.0675,
  cumberland: 0.07,
  currituck: 0.0675,
  dare: 0.0675,
  davidson: 0.07,
  davie: 0.0675,
  duplin: 0.07,
  durham: 0.075,
  edgecombe: 0.07,
  forsyth: 0.07,
  franklin: 0.0675,
  gaston: 0.07,
  gates: 0.0675,
  graham: 0.07,
  granville: 0.0675,
  greene: 0.07,
  guilford: 0.0675,
  halifax: 0.07,
  harnett: 0.07,
  haywood: 0.07,
  henderson: 0.0675,
  hertford: 0.07,
  hoke: 0.0675,
  hyde: 0.0675,
  iredell: 0.0675,
  jackson: 0.07,
  johnston: 0.0675,
  jones: 0.07,
  lee: 0.07,
  lenoir: 0.0675,
  lincoln: 0.07,
  macon: 0.0675,
  madison: 0.07,
  martin: 0.07,
  mcdowell: 0.0675,
  mecklenburg: 0.0725,
  mitchell: 0.0675,
  montgomery: 0.07,
  moore: 0.07,
  nash: 0.0675,
  newhanover: 0.07,
  northampton: 0.0675,
  onslow: 0.07,
  orange: 0.075,
  pamlico: 0.0675,
  pasquotank: 0.07,
  pender: 0.0675,
  perquimans: 0.0675,
  person: 0.0675,
  pitt: 0.07,
  polk: 0.0675,
  randolph: 0.07,
  richmond: 0.0675,
  robeson: 0.07,
  rockingham: 0.07,
  rowan: 0.07,
  rutherford: 0.07,
  sampson: 0.07,
  scotland: 0.0675,
  stanly: 0.07,
  stokes: 0.0675,
  surry: 0.07,
  swain: 0.07,
  transylvania: 0.0675,
  tyrrell: 0.0675,
  union: 0.0675,
  vance: 0.0675,
  wake: 0.0725,
  warren: 0.0675,
  washington: 0.07,
  watauga: 0.0675,
  wayne: 0.0675,
  wilkes: 0.07,
  wilson: 0.0675,
  yadkin: 0.0675,
  yancey: 0.0675,
};

// Quantity Functionality

export const getInventoryKey = (size) => {
  return SIZE_KEY_MAP[size] || null;
};

export const getStockBySize = (inventory = {}, size) => {
  const inventoryKey = getInventoryKey(size);

  if (!inventoryKey) return 0;

  return Number(inventory?.[inventoryKey] || 0);
};

export const getSizeOptions = (inventory = {}) => {
  return SIZE_ORDER.map((size) => {
    const stock = getStockBySize(inventory, size);

    return {
      size,
      stock,
      inStock: stock > 0,
    };
  });
};

export const getMaxQuantity = (stock, maxLimit = 5) => {
  return Math.max(1, Math.min(Number(stock || 0), maxLimit));
};

export const getQuantityOptions = (stock, maxLimit = 5) => {
  const maxQuantity = getMaxQuantity(stock, maxLimit);
  return Array.from({ length: maxQuantity }, (_, i) => i + 1);
};

export const isCompletelyOutOfStock = (inventory = {}) => {
  return getSizeOptions(inventory).every((option) => option.stock === 0);
};

export const PRODUCT_IMAGE_TYPE_ORDER = [
  "full-body-front",
  "close-up-front",
  "close-up-back",
  "detail-1",
  "detail-2",
  "full-body-back",
];

export const sortProductImages = (images = []) => {
  const order = new Map(PRODUCT_IMAGE_TYPE_ORDER.map((type, index) => [type, index]));

  return images
    .filter((image) => order.has(image.type))
    .slice()
    .sort((a, b) => order.get(a.type) - order.get(b.type));
};

export const getPrimaryImage = (style) => {
  if (!style || !Array.isArray(style.images)) return null;

  // full-body-front is always the storefront primary image.
  return style.images.find((img) => img.type === "full-body-front") || null;
};

export const getMatchingStyle = (catalogProduct, lookupKey) => {
  if (!catalogProduct || !lookupKey) return null;
  return catalogProduct.styles?.find((style) => style.lookupKey === lookupKey) || null;
};

export const getCatalogProduct = (catalogProducts, productId) => {
  return catalogProducts.find((product) => product.productId === productId) || null;
};
